// Zustand store + tiny API client. The bearer token + worker URL + account id
// live in localStorage so a refresh keeps the session. There is no cookie,
// no server-side session — auth is fully on the operator's side.

import { create } from 'zustand';

const LS_KEYS = {
  workerUrl: 'bvs.workerUrl',
  bearer: 'bvs.bearer',
  accountId: 'bvs.accountId',
  deviceId: 'bvs.deviceId',
};

function readLs(k, fallback = '') {
  try {
    return localStorage.getItem(k) || fallback;
  } catch {
    return fallback;
  }
}

function writeLs(k, v) {
  try {
    if (v == null || v === '') localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* private mode */
  }
}

export const useSession = create((set, get) => ({
  workerUrl: readLs(LS_KEYS.workerUrl),
  bearer: readLs(LS_KEYS.bearer),
  accountId: readLs(LS_KEYS.accountId),
  deviceId: readLs(LS_KEYS.deviceId, 'dashboard-spa'),

  set(updates) {
    set((s) => ({ ...s, ...updates }));
    if ('workerUrl' in updates) writeLs(LS_KEYS.workerUrl, updates.workerUrl);
    if ('bearer' in updates) writeLs(LS_KEYS.bearer, updates.bearer);
    if ('accountId' in updates) writeLs(LS_KEYS.accountId, updates.accountId);
    if ('deviceId' in updates) writeLs(LS_KEYS.deviceId, updates.deviceId);
  },

  clear() {
    writeLs(LS_KEYS.workerUrl, '');
    writeLs(LS_KEYS.bearer, '');
    writeLs(LS_KEYS.accountId, '');
    set({ workerUrl: '', bearer: '', accountId: '' });
  },

  isReady() {
    const s = get();
    return Boolean(s.workerUrl && s.bearer && s.accountId);
  },
}));

export function apiUrl(path) {
  const base = (useSession.getState().workerUrl || '').replace(/\/$/, '');
  return `${base}${path}`;
}

export async function apiFetch(path, options = {}) {
  const s = useSession.getState();
  if (!s.workerUrl || !s.bearer || !s.accountId) {
    throw new Error('not logged in');
  }
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${s.bearer}`,
    'X-Account-Id': s.accountId,
    'X-Device-Id': s.deviceId || 'dashboard-spa',
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body !== 'string') {
    headers['Content-Type'] = 'application/json';
    options = { ...options, body: JSON.stringify(options.body) };
  }
  const res = await fetch(apiUrl(path), { ...options, headers });
  const text = await res.text();
  let parsed;
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = { raw: text };
  }
  if (!res.ok) {
    const err = new Error(parsed?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = parsed;
    throw err;
  }
  return parsed;
}
